import {createRouter, createWebHistory} from 'vue-router';
import ChinaDentiumSignin from "@/views/SignIn/Signin.vue";
import ProductsList from '../views/Products/ProductsList.vue';
import CertificateList from '../views/Certificate/CertificateList.vue';
import CustomizeMenu from "@/views/Gzh/CustomizeMenu.vue";
import AutoReplayManagement from "@/views/Gzh/AutoReplayManagement.vue";
import GzhAccountManagement from "@/views/Gzh/GzhAccountManagement.vue";
import FollowerList from '../views/Follower/FollowerList.vue';
import FollowerStats from '../views/Follower/FollowerStats.vue';
import CertificateWorkOrder from '../views/CertificateWorkOrder/index.vue';
import {RouterEnum} from "@/enums/router.enums";
// 병원/기공소 관리 (SPEC-CHINA-ADMIN-HOSPITAL-001)
import CompanyList from '../views/CompanyList/CompanyList.vue';
// 제조사 실무자 통합 관리 (SPEC-CHINA-MFR-STAFF-ACCOUNT-001)
import MfrStaffManage from '../views/MfrStaffManage/MfrStaffManage.vue';
// EcountEtl 운영 콘솔 (SPEC-ECOUNT-ETL-OPS-CONSOLE-001)
import EcountEtlDashboard from '../views/EcountEtl/Dashboard.vue';
// SPEC-ECOUNT-ETL-CRON-CONSOLE-002 Phase 3: 신규 ERP 콘솔 (마스터/cron/수동재실행/이력)
import EcountErpConsole from '../views/EcountEtl/EcountErpConsole.vue';
import EcountEtlRunsList from '../views/EcountEtl/RunsList.vue';
import EcountEtlRunDetail from '../views/EcountEtl/RunDetail.vue';
import EcountEtlDeadLetters from '../views/EcountEtl/DeadLetters.vue';
import EcountEtlProductsList from '../views/EcountEtl/ProductsList.vue';
import EcountEtlTransactionsList from '../views/EcountEtl/TransactionsList.vue';
// 거래상세 (SPEC-CHINA-ECOUNT-SALES-LINES-001)
import EcountEtlSalesLines from '../views/EcountEtl/SalesLines.vue';
// Lot/Batch 통합 추적 (SPEC-CHINA-LOT-BATCH-TRACEABILITY-001)
import LotTrace from '../views/LotTrace/LotTrace.vue';
import { useFeatureFlag } from '../views/EcountEtl/composables/useFeatureFlag.js';
import {jwtDecoder, isOperator} from "@/utils/utils";
import { isPublicRoutePath, resolveGuestRedirect } from './publicRoutes';
import ForcePasswordChange from '../views/SignIn/ForcePasswordChange.vue';
// 메뉴 미배정 계정 안내 화면
import NoMenuAccess from '../views/SignIn/NoMenuAccess.vue';
import { resolveLandingPath, resolveLandingPathForUser, userHasMenuLink, userHasMenuLinkPrefix, NO_MENU_PATH } from '@/utils/landing';
import { message } from 'ant-design-vue';
import i18n from '@/i18n';
// store는 router guard에서 getPrivileges 호출에 사용 (순환 의존 없음 — store는 router를 import하지 않음)
import store from '@/store';

const companyId = jwtDecoder(localStorage.getItem('accessTokenManu'));
const isMobile = window.innerWidth < 768;

// @MX:NOTE: [AUTO] redirectDefaultPage는 인증 없는 catch-all 전용. 인증된 사용자는 landing.js resolveLandingPath 사용.
const redirectDefaultPage = `${RouterEnum.ProductManagement.path}`;

const routes = [
    {
        // '/' 직접 접근은 router.beforeEach guard에서 동적으로 처리한다 (권한 첫 메뉴 또는 /no-menu).
        // 정적 redirect는 제거 — 인증 여부와 무관하게 특정 경로로 보내는 버그가 있었음.
        path: '/',
        name: 'Root',
        // guard에서 next(landingPath)로 처리되므로 실제로 렌더링될 일 없음.
        // 빈 컴포넌트 대신 NoMenuAccess로 안전하게 폴백.
        component: NoMenuAccess,
    },
    {
        // 잘못된 URL(catch-all): '/'로 보내 가드의 동적 landing(첫 메뉴/안내/로그인)을 일관되게 태운다.
        path: '/:pathMatch(.*)*',
        redirect: '/',
    },
    {   // 로그인
        path: RouterEnum.SignIn.path,
        name: RouterEnum.SignIn.name,
        component: ChinaDentiumSignin,
    },
    { // 제품관리
        path: RouterEnum.ProductManagement.path,
        name: RouterEnum.ProductManagement.name,
        children: [
            {
                path: '',
                name: RouterEnum.ProductManagement.name,
                component: ProductsList,
            },
        ]
    },
    { // 발행 내역 조회
        path: RouterEnum.Certificate.path,
        name: RouterEnum.Certificate.name,
        redirect: `${RouterEnum.Certificate.path}/${RouterEnum.Certificate.CertificateList.path}`,
        children: [
            { // 발행 내역
                path: RouterEnum.Certificate.CertificateList.path,
                name: RouterEnum.Certificate.CertificateList.name,
                component: CertificateList
            }
        ]
    },
    {   // 공중계정 메뉴설정
        path: RouterEnum.Customize.path,
        name: RouterEnum.Customize.name,
        children: [
            {
                path: RouterEnum.Customize.Menu.path,
                name: RouterEnum.Customize.Menu.name,
                component: CustomizeMenu,
            },
            {
                path: RouterEnum.Customize.AutoReplay.path,
                name: RouterEnum.Customize.AutoReplay.name,
                component: AutoReplayManagement,
            },
            {
                path: RouterEnum.Customize.Accounts.path,
                name: RouterEnum.Customize.Accounts.name,
                component: GzhAccountManagement,
            },
        ]
    },
    { // 병원 도메인 작업지시서 (조회 전용, SPEC-CHINA-HOSPITAL-WO-READ-001)
        path: RouterEnum.HospitalWorkOrders.path,
        name: RouterEnum.HospitalWorkOrders.name,
        redirect: `${RouterEnum.HospitalWorkOrders.path}/${RouterEnum.HospitalWorkOrders.Manual.path}`,
        children: [
            {
                path: RouterEnum.HospitalWorkOrders.Manual.path,
                name: RouterEnum.HospitalWorkOrders.Manual.name,
                component: () => import('../views/HospitalWorkOrders/HospitalOrderList.vue'),
                props: { kind: 'manual' },
            },
            {
                path: RouterEnum.HospitalWorkOrders.Hengjiu.path,
                name: RouterEnum.HospitalWorkOrders.Hengjiu.name,
                component: () => import('../views/HospitalWorkOrders/HospitalOrderList.vue'),
                props: { kind: 'hengjiu' },
            },
        ]
    },
    { // 팔로워 관리
        path: RouterEnum.Follower.path,
        name: RouterEnum.Follower.name,
        redirect: `${RouterEnum.Follower.path}/${RouterEnum.Follower.FollowerList.path}`,
        children: [
            {
                path: RouterEnum.Follower.FollowerList.path,
                name: RouterEnum.Follower.FollowerList.name,
                component: FollowerList,
            },
            {
                path: RouterEnum.Follower.FollowerStats.path,
                name: RouterEnum.Follower.FollowerStats.name,
                component: FollowerStats,
            }
        ]
    },
    { // 인증서 관리
        path: RouterEnum.Certificate.path,
        name: RouterEnum.Certificate.name,
        redirect: `${RouterEnum.Certificate.path}/${RouterEnum.Certificate.CertificateList.path}`,
        children: [
            {
                path: RouterEnum.Certificate.CertificateList.path,
                name: RouterEnum.Certificate.CertificateList.name,
                component: CertificateList
            }
        ]
    },
    { // 보철 인증서 발행 (Work Order)
        // privileges link는 /dental-laboratory/certificate-delivery (한국 백엔드 라우트 패턴) 로 등록되어 있어
        // alias로 함께 매핑. privileges 데이터를 회사별로 고치지 않고 frontend router에서 흡수.
        path: RouterEnum.CertificateWorkOrder.path,
        name: RouterEnum.CertificateWorkOrder.name,
        component: CertificateWorkOrder,
        alias: ['/dental-laboratory/certificate-delivery'],
    },
    { // privileges legacy link 호환: /certificate/list → /china/certificate/list redirect
        path: '/certificate/list',
        redirect: `${RouterEnum.Certificate.path}/${RouterEnum.Certificate.CertificateList.path}`,
    },
    // (제거) /info 사업자 정보 관리 — 병원·기공소 공용 /company-profile(企业信息管理)로 대체.
    // 한국 이관 잔재 메뉴였고, 운영 privileges 전건에서 /info 부여 0건 확인 후 화면·라우트를 폐기했다(2026-08-13).
    // 병원관리 (SPEC-CHINA-ADMIN-HOSPITAL-001) — catch-all보다 먼저 등록 (REQ-ROUTE-003)
    {
        path: RouterEnum.Hospital.path,
        name: RouterEnum.Hospital.name,
        redirect: `${RouterEnum.Hospital.path}/${RouterEnum.Hospital.List.path}`,
        children: [
            {
                path: RouterEnum.Hospital.List.path,
                name: RouterEnum.Hospital.List.name,
                component: CompanyList,
                props: { type: 'HOSPITAL' },
            },
        ],
    },
    // 기공소관리 (SPEC-CHINA-ADMIN-HOSPITAL-001) — catch-all보다 먼저 등록 (REQ-ROUTE-003)
    {
        path: RouterEnum.DentalLab.path,
        name: RouterEnum.DentalLab.name,
        redirect: `${RouterEnum.DentalLab.path}/${RouterEnum.DentalLab.List.path}`,
        children: [
            {
                path: RouterEnum.DentalLab.List.path,
                name: RouterEnum.DentalLab.List.name,
                component: CompanyList,
                props: { type: 'DENTAL_LABORATORY' },
            },
        ],
    },
    // 멤버십 관리 — 폐기, /iam으로 리다이렉트 (SPEC-CHINA-IAM-CONSOLE-001)
    // 구 화면(MembershipList.vue, 탭 accounts/memberships)은 삭제됐다. 어느 탭을 향하던
    // 링크였든 신규 콘솔의 사용자 탭(모든 계정 단일 목록)으로 보낸다 — 흡수 대상이 그 탭 하나뿐이므로.
    {
        path: RouterEnum.Memberships.path,
        name: RouterEnum.Memberships.name,
        redirect: () => ({ path: RouterEnum.IamConsole.path, query: { tab: 'users' } }),
    },
    // IAM 콘솔 — 계정·소속·권한 통합 단일 화면, 운영자 전용 (SPEC-CHINA-IAM-CONSOLE-001)
    // 사이드바 메뉴는 DB(menus/privileges)에서 별도 등록 — 라우트만 여기 선언
    {
        path: RouterEnum.IamConsole.path,
        name: RouterEnum.IamConsole.name,
        component: () => import('../views/IamConsole/IamConsole.vue'),
    },
    // 자기 회사 정보 수정(self-service) — 병원·기공소 공용 (SPEC-CHINA-IAM-CORE-001)
    // 사이드바 메뉴는 DB(menus/privileges)에서 별도 등록 — 라우트만 여기 선언
    {
        path: RouterEnum.CompanyProfile.path,
        name: RouterEnum.CompanyProfile.name,
        component: () => import('../views/CompanyProfile/CompanyProfile.vue'),
    },
    // 구 병원 전용 경로 — 폐기, /company-profile로 리다이렉트 (Memberships와 동일 관례)
    {
        path: RouterEnum.HospitalProfile.path,
        name: RouterEnum.HospitalProfile.name,
        redirect: RouterEnum.CompanyProfile.path,
    },
    // EcountEtl 운영 콘솔 (SPEC-ECOUNT-ETL-OPS-CONSOLE-001)
    {
        path: RouterEnum.EcountEtl.path,
        name: RouterEnum.EcountEtl.name,
        redirect: `${RouterEnum.EcountEtl.path}/${RouterEnum.EcountEtl.Dashboard.path}`,
        children: [
            {
                // 신규 ERP 콘솔 (마스터/cron/수동재실행/이력) — 사이드바 'ECOUNT ERP 관리' 진입점
                path: RouterEnum.EcountEtl.Dashboard.path,
                name: RouterEnum.EcountEtl.Dashboard.name,
                component: EcountErpConsole,
            },
            {
                // 기존 Dashboard.vue 보존 (점진 마이그레이션, 1~2주 후 deprecation)
                path: 'dashboard-legacy',
                name: 'EcountEtlDashboardLegacy',
                component: EcountEtlDashboard,
            },
            {
                path: RouterEnum.EcountEtl.Runs.path,
                name: RouterEnum.EcountEtl.Runs.name,
                component: EcountEtlRunsList,
            },
            {
                path: RouterEnum.EcountEtl.RunDetail.path,
                name: RouterEnum.EcountEtl.RunDetail.name,
                component: EcountEtlRunDetail,
            },
            {
                path: RouterEnum.EcountEtl.DeadLetters.path,
                name: RouterEnum.EcountEtl.DeadLetters.name,
                component: EcountEtlDeadLetters,
            },
            {
                path: RouterEnum.EcountEtl.Products.path,
                name: RouterEnum.EcountEtl.Products.name,
                component: EcountEtlProductsList,
            },
            {
                path: RouterEnum.EcountEtl.Transactions.path,
                name: RouterEnum.EcountEtl.Transactions.name,
                component: EcountEtlTransactionsList,
            },
            // 거래상세 (SPEC-CHINA-ECOUNT-SALES-LINES-001)
            {
                path: RouterEnum.EcountEtl.SalesLines.path,
                name: RouterEnum.EcountEtl.SalesLines.name,
                component: EcountEtlSalesLines,
            },
            // SyncTrigger: feature flag 활성 시에만 동적 import
            ...(useFeatureFlag('VUE_APP_ECOUNT_ETL_SYNC_TRIGGER') ? [{
                path: RouterEnum.EcountEtl.SyncTrigger.path,
                name: RouterEnum.EcountEtl.SyncTrigger.name,
                component: () => import('../views/EcountEtl/SyncTrigger.vue'),
            }] : []),
        ],
    },
    // Lab Orders 헝지우 작업지시서 (SPEC-CHINA-LAB-ORDERS-READ-001)
    // + 수기 작업지시서 목록 (SPEC-CHINA-LAB-MANUAL-WORKORDER-LIST-001)
    {
        path: RouterEnum.LabOrders.path,
        name: RouterEnum.LabOrders.name,
        redirect: `${RouterEnum.LabOrders.path}/${RouterEnum.LabOrders.Hengjiu.path}`,
        children: [
            {
                path: RouterEnum.LabOrders.Hengjiu.path,
                name: RouterEnum.LabOrders.Hengjiu.name,
                component: () => import('../views/LabOrders/HengjiuWorkOrders.vue'),
            },
            {
                path: RouterEnum.LabOrders.Manual.path,
                name: RouterEnum.LabOrders.Manual.name,
                component: () => import('../views/LabOrders/ManualWorkOrders.vue'),
            },
        ],
    },
    // Lot/Batch 통합 추적 (SPEC-CHINA-LOT-BATCH-TRACEABILITY-001 REQ-FE-TRC-001)
    {
        path: RouterEnum.LotTrace.path + '/:value',
        name: RouterEnum.LotTrace.name,
        component: LotTrace,
    },
    // 흐름 현황판 (SPEC-CHINA-LOT-FLOW-DASHBOARD-001 REQ-FE-DASH-001~002)
    {
        path: RouterEnum.LotFlowDashboard.path,
        name: RouterEnum.LotFlowDashboard.name,
        component: () => import('../views/LotFlow/LotFlowDashboard.vue'),
    },
    // 딜러사 전용 대시보드 (SPEC-CHINA-DEALER-DASHBOARD-001 REQ-FE-001) — 로그인 랜딩(privileges 첫 메뉴 배치로 달성)
    {
        path: RouterEnum.DealerDashboard.path,
        name: RouterEnum.DealerDashboard.name,
        component: () => import('../views/DealerDashboard/DealerDashboard.vue'),
    },
    // 덴탈맥스 본사 전용 대시보드 (SPEC-CHINA-HQ-DASHBOARD-001) — 메뉴 배정된 계정만 접근(RequireMenu('/hq-dashboard'))
    {
        path: RouterEnum.HqDashboard.path,
        name: RouterEnum.HqDashboard.name,
        component: () => import('../views/HqDashboard/HqDashboard.vue'),
    },
    // 실물인증서 카드 발번·이력·정품조회 (SPEC-CHINA-CERT-CARD-ISSUE-001)
    {
        path: RouterEnum.CertificateCard.path,
        name: RouterEnum.CertificateCard.name,
        redirect: `${RouterEnum.CertificateCard.path}/${RouterEnum.CertificateCard.Issue.path}`,
        children: [
            {
                path: RouterEnum.CertificateCard.Issue.path,
                name: RouterEnum.CertificateCard.Issue.name,
                component: () => import('../views/CertificateCard/CertificateCardIssue.vue'),
            },
            {
                path: RouterEnum.CertificateCard.Batches.path,
                name: RouterEnum.CertificateCard.Batches.name,
                component: () => import('../views/CertificateCard/CertificateCardBatches.vue'),
            },
            {
                path: RouterEnum.CertificateCard.Verify.path,
                name: RouterEnum.CertificateCard.Verify.name,
                component: () => import('../views/CertificateCard/CertificateCardVerify.vue'),
            },
            // 견적표 관리 화면 (SPEC-CHINA-CERT-CARD-QUOTATION-UX-001)
            {
                path: RouterEnum.CertificateCard.Quotation.path,
                name: RouterEnum.CertificateCard.Quotation.name,
                component: () => import('../views/CertificateCard/CertificateCardQuotation.vue'),
            },
        ],
    },
    // F1: 최초 로그인 비밀번호 강제 변경 — SPEC-CHINA-ACCTSEC-001
    {
        path: '/force-password-change',
        name: 'ForcePasswordChange',
        component: ForcePasswordChange,
    },
    // 제품라인 라벨 매핑 관리 (SPEC-CHINA-PRODUCT-LINE-MAPPING-001)
    // 사이드바 메뉴는 DB(menus/privileges)에서 별도 등록 — 라우트만 여기 선언
    {
        path: RouterEnum.ProductLineMapping.path,
        name: RouterEnum.ProductLineMapping.name,
        component: () => import('../views/ProductLineMapping/index.vue'),
    },
    // 위챗 사용자 정합 관측 (SPEC-CHINA-FOLLOW-SYNC-001 M4)
    // XINLAIAN 전용 — 사이드바 노출은 xinlaian.menus role:XINLAIAN 등록으로 통제,
    // 데이터는 BE @Roles(XINLAIAN)가 최종 차단(테넌트 진입 시 403 배너).
    {
        path: RouterEnum.FollowSync.path,
        name: RouterEnum.FollowSync.name,
        component: () => import('../views/FollowSync/FollowSyncDashboard.vue'),
    },
    // 위챗 오픈플랫폼 운영 콘솔 (SPEC-CHINA-WECHAT-OPEN-PLATFORM-001 F1/F2, 운영자 전용)
    // 이중 관문: 메뉴 보유(PRIVILEGE_GUARDED_MENU_PATHS, 전역 가드) + isOperator(아래 beforeEnter).
    // 데이터는 BE @Roles(XINLAIAN)가 최종 차단한다.
    {
        path: RouterEnum.WechatOpen.path,
        name: RouterEnum.WechatOpen.name,
        component: () => import('../views/WechatOpen/WechatOpenConsole.vue'),
        beforeEnter: async (to) => {
            if (isOperator()) return true;
            // 루프 방지: landing 이 이 경로 자신이면 /no-menu 로 보낸다
            message.warning(i18n.global.t('route_guard_no_menu_permission'));
            const landingPath = await resolveLandingPathForUser(store, false);
            return landingPath === to.path ? RouterEnum.NoMenuAccess.path : landingPath;
        },
    },
    // 公众号 관리자용 공개 授权 페이지 (무로그인) — 가드가 로그인 여부와 무관하게 통과시킨다(publicRoutes.js).
    // callback 을 :token 보다 먼저 둔다.
    {
        path: RouterEnum.WxAuthorizeCallback.path,
        name: RouterEnum.WxAuthorizeCallback.name,
        component: () => import('../views/WechatOpen/AuthorizeCallback.vue'),
    },
    {
        path: RouterEnum.WxAuthorizeInvite.path,
        name: RouterEnum.WxAuthorizeInvite.name,
        component: () => import('../views/WechatOpen/InviteAuthorize.vue'),
        props: true,
    },
    // 메뉴 미배정 계정 안내 화면 — privileges에 메뉴가 없을 때 진입
    {
        path: RouterEnum.NoMenuAccess.path,
        name: RouterEnum.NoMenuAccess.name,
        component: NoMenuAccess,
    },
    // 제조사 실무자 통합 관리 (SPEC-CHINA-MFR-STAFF-ACCOUNT-001)
    // privileges link: /mfr-staff-manage — 사이드바에서 등록 후 진입
    // [2026-08-04] SPEC-CHINA-IAM-CONSOLE-001: 운영자(XINLAIAN)의 딥링크 잔존 접근을 /iam으로 차단.
    // 제조사 마스터/실무자 본인의 셀프서비스 접근은 그대로 둔다 — 그들에겐 정당한 진입점(연관 탭 포함).
    {
        path: RouterEnum.MfrStaffManage.path,
        name: RouterEnum.MfrStaffManage.name,
        component: MfrStaffManage,
        beforeEnter: (to, from, next) => {
            if (isOperator()) {
                next({ path: RouterEnum.IamConsole.path });
                return;
            }
            next();
        },
    },
    // GEO 지표관리 (SPEC-CHINA-GEO-ADMIN-001 Phase 1) — catch-all보다 먼저 등록
    // 사이드바 메뉴는 DB(menus/privileges)에서 별도 등록 — 라우트만 여기 선언
    {
        path: RouterEnum.Geo.path,
        name: RouterEnum.Geo.name,
        // [P1 IA 완주, 2026-08-14] GEO 진입 기본 랜딩 = 브랜드 허브(셀렉터).
        // 허브가 워크스페이스의 대문이고 대시보드(수집 상태)는 플랫폼 운영 화면이다 —
        // 종전 dashboard 랜딩은 "허브 vs 대시보드 어느 쪽이 정본인가"의 혼동을 만들었다(진단 F1).
        redirect: `${RouterEnum.Geo.path}/${RouterEnum.Geo.Hub.path}`,
        children: [
            {
                path: RouterEnum.Geo.Dashboard.path,
                name: RouterEnum.Geo.Dashboard.name,
                component: () => import('../views/Geo/GeoDashboard.vue'),
            },
            // 엔진·세션 관리(시스템 설정) — GeoDashboard.vue에서 CRUD 분리(IA 점검 P0)
            {
                path: RouterEnum.Geo.EngineSessions.path,
                name: RouterEnum.Geo.EngineSessions.name,
                component: () => import('../views/Geo/EngineSessionManage.vue'),
            },
            {
                path: RouterEnum.Geo.QuestionSets.path,
                name: RouterEnum.Geo.QuestionSets.name,
                component: () => import('../views/Geo/QuestionSetList.vue'),
            },
            {
                path: RouterEnum.Geo.Hospitals.path,
                name: RouterEnum.Geo.Hospitals.name,
                component: () => import('../views/Geo/HospitalAliasList.vue'),
            },
            {
                path: RouterEnum.Geo.Runs.path,
                name: RouterEnum.Geo.Runs.name,
                component: () => import('../views/Geo/RunMonitor.vue'),
            },
            // 수집 회차 상세 — RunMonitor/GeoDashboard 드로어를 대체(SPEC-CHINA-GEO-ADMIN-001)
            {
                path: RouterEnum.Geo.RunDetail.path,
                name: RouterEnum.Geo.RunDetail.name,
                component: () => import('../views/Geo/RunDetail.vue'),
            },
            // 캔버스형 편집 v2 (feature/geo-cms-editor-v2) — v1과 병행, 기본 진입은 계속 v1
            {
                path: RouterEnum.Geo.CmsEditV2.path,
                name: RouterEnum.Geo.CmsEditV2.name,
                component: () => import('../views/Geo/CmsContentEditV2.vue'),
            },
            {
                path: RouterEnum.Geo.SourceText.path,
                name: RouterEnum.Geo.SourceText.name,
                component: () => import('../views/Geo/SourceTextManage.vue'),
            },
            // LLM 사용량 조회 (SPEC-CHINA-GEO-LLM-GATEWAY-001)
            {
                path: RouterEnum.Geo.LlmUsage.path,
                name: RouterEnum.Geo.LlmUsage.name,
                component: () => import('../views/Geo/LlmUsage.vue'),
            },
            {
                path: RouterEnum.Geo.MediaReview.path,
                name: RouterEnum.Geo.MediaReview.name,
                component: () => import('../views/Geo/MediaReview.vue'),
            },
            {
                path: RouterEnum.Geo.Compliance.path,
                name: RouterEnum.Geo.Compliance.name,
                component: () => import('../views/Geo/CompliancePolicy.vue'),
            },
            // 브랜드 허브 (SPEC-CHINA-GEO-HUB-001 M2) — 셀렉터/검수 패널
            {
                path: RouterEnum.Geo.Hub.path,
                name: RouterEnum.Geo.Hub.name,
                component: () => import('../views/Geo/BrandHubSelector.vue'),
            },
            // 브랜드 허브 상세 — 6스테이션 워크스페이스(companyId 스코프)
            {
                path: RouterEnum.Geo.HubDetail.path,
                name: RouterEnum.Geo.HubDetail.name,
                component: () => import('../views/Geo/BrandHub.vue'),
            },
        ],
    },
    // Dealer GSP ETL (SPEC-DEALER-GSP-ETL-001 Phase β)
    {
        path: RouterEnum.DealerGsp.path,
        name: RouterEnum.DealerGsp.name,
        redirect: `${RouterEnum.DealerGsp.path}/${RouterEnum.DealerGsp.PurchaseInbounds.path}`,
        children: [
            {
                path: RouterEnum.DealerGsp.PurchaseInbounds.path,
                name: RouterEnum.DealerGsp.PurchaseInbounds.name,
                component: () => import('../views/DealerGsp/PurchaseInbounds.vue'),
            },
            {
                path: RouterEnum.DealerGsp.SalesOutbounds.path,
                name: RouterEnum.DealerGsp.SalesOutbounds.name,
                component: () => import('../views/DealerGsp/SalesOutbounds.vue'),
            },
            {
                path: RouterEnum.DealerGsp.InventorySnapshots.path,
                name: RouterEnum.DealerGsp.InventorySnapshots.name,
                component: () => import('../views/DealerGsp/InventorySnapshots.vue'),
            },
            {
                path: RouterEnum.DealerGsp.EtlRuns.path,
                name: RouterEnum.DealerGsp.EtlRuns.name,
                component: () => import('../views/DealerGsp/EtlRunsList.vue'),
            },
            {
                path: RouterEnum.DealerGsp.CronConsole.path,
                name: RouterEnum.DealerGsp.CronConsole.name,
                component: () => import('../views/DealerGsp/CronConsole.vue'),
            },
        ],
    },
];

const router = createRouter({
    history: createWebHistory(process.env.BASE_URL),
    routes,
    linkActiveClass: 'active',
});

// privileges 메뉴 보유 시에만 접근 가능한 라우트 (URL 직접 접근 게이팅 대상)
const PRIVILEGE_GUARDED_MENU_PATHS = [
    RouterEnum.CertificateWorkOrder.path, // 수기 작업지시서 작성 위저드 (기공소 전용)
    RouterEnum.WechatOpen.path, // 위챗 오픈플랫폼 (운영자 전용 — 메뉴 보유 + isOperator 이중 관문)
];

// [SPEC-CHINA-GEO-MEASURE-TRUTH-001 REQ-MT-062] 계열(prefix) 단위 게이팅 — fail-closed.
// GEO 화면 대부분은 딥링크 전용(정확히 일치하는 메뉴 문서가 없다)이라 경로별 목록으로는
// 게이팅이 불가능하다. 그 계열 메뉴(/china/geo/*)를 하나라도 보유해야 계열 전체 접근 허용.
const PRIVILEGE_GUARDED_MENU_PREFIXES = [
    RouterEnum.Geo.path, // /china/geo — GEO 워크스페이스 전체 (운영자 전용, 향후 테넌트 개방 시에도 메뉴 보유가 관문)
];

// @MX:WARN: [AUTO] router.beforeEach — 리다이렉트 루프 방지 로직 포함. 변경 시 루프 조건 재검증 필수.
// @MX:REASON: '/'·'/signin'·'/no-menu' 세 경로가 서로 redirect할 수 있으며, guard 내 await getPrivileges가 추가되어 무한루프 위험 존재.
router.beforeEach(async (to, from, next) => {
    // 공개 授权 페이지(/wx-authorize/*) — 로그인 여부·비밀번호 강제변경 게이트와 무관하게 항상 통과.
    // 이 경로에서는 어떤 redirect 도 하지 않으므로 다른 경로와 서로 redirect 하는 고리가 생기지 않는다.
    if (isPublicRoutePath(to.path)) {
        next();
        return;
    }
    const accessToken = localStorage.getItem('accessTokenManu');
    const pwChangeRequired = localStorage.getItem('pwChangeRequiredManu') === 'Y';
    // '다음에 변경하기' 선택 시 이번 세션에 한해 게이트 통과 (탭 닫기/재로그인 시 다시 강제)
    const pwChangeSkipped = sessionStorage.getItem('pwChangeSkippedManu') === 'Y';

    if (accessToken) {
        // F1: 비밀번호 강제변경 게이트 — 변경 완료 전까지 /force-password-change 외 접근 차단
        // 로그아웃(/signin)은 항상 허용. '다음에 변경하기'(skip) 시에는 게이트를 통과시킨다.
        if (pwChangeRequired && !pwChangeSkipped) {
            if (to.path === '/force-password-change' || to.path === '/signin') {
                next();
            } else {
                next('/force-password-change');
            }
            return;
        }

        // /force-password-change 직접 접근 처리:
        //   - 아직 변경 필요(skip 상태) → 폼 재접근 허용 (사용자가 마음을 바꿔 바로 변경 가능)
        //   - 변경 완료(pwChangeRequired=false) → 동적 landing으로 리다이렉트
        if (to.path === '/force-password-change') {
            if (pwChangeRequired) {
                next();
            } else {
                // 이미 landing 페이지로 이동 중이면 루프 방지
                next(redirectDefaultPage);
            }
            return;
        }

        // '/' 직접 접근: 권한 첫 메뉴(또는 /no-menu)로 동적 리다이렉트 (로그인과 일관)
        // 루프 방지: resolveLandingPathForUser는 실제 메뉴 경로 또는 /no-menu만 반환 → 재진입 시 통과
        if (to.path === '/') {
            const landingPath = await resolveLandingPathForUser(store, false);
            next(landingPath);
            return;
        }

        if (to.path === '/signin' || to.path === '/signup') {
            // 이미 로그인된 상태: 정적 default 대신 동적 landing으로 (로그인과 일관)
            const landingPath = await resolveLandingPathForUser(store, false);
            next(landingPath);
            return;
        }

        // 권한 게이팅 라우트: privileges 메뉴에 없는 계정의 URL 직접 접근 차단
        // 수기 작업지시서 작성 위저드는 기공소 전용 — 제조사 계정은 메뉴 미보유 (2026-07-16 감사 P0-1 결정)
        // 루프 방지: 차단 시 landing(실제 메뉴 경로 또는 /no-menu)으로만 보낸다 — 게이팅 경로로 재진입하지 않음
        const guardedMenuPath = PRIVILEGE_GUARDED_MENU_PATHS.find(
            (p) => to.path === p || to.path.startsWith(`${p}/`)
        );
        if (guardedMenuPath) {
            const allowed = await userHasMenuLink(store, guardedMenuPath);
            if (!allowed) {
                message.warning(i18n.global.t('route_guard_no_menu_permission'));
                const landingPath = await resolveLandingPathForUser(store, false);
                next(landingPath);
                return;
            }
        }

        // [REQ-MT-062] 계열 게이팅 — 위 경로별 게이팅과 동일한 루프 방지 원칙:
        // 차단 시 landing(실제 메뉴 경로 또는 /no-menu)으로만 보내고 게이팅 경로로 재진입하지 않는다.
        const guardedPrefix = PRIVILEGE_GUARDED_MENU_PREFIXES.find(
            (p) => to.path === p || to.path.startsWith(`${p}/`)
        );
        if (guardedPrefix) {
            const allowed = await userHasMenuLinkPrefix(store, guardedPrefix);
            if (!allowed) {
                message.warning(i18n.global.t('route_guard_no_menu_permission'));
                const landingPath = await resolveLandingPathForUser(store, false);
                next(landingPath);
                return;
            }
        }

        next();
    } else {
        // 비로그인: /signin, /reset-pwd 만 통과(공개 授权 페이지는 위에서 이미 통과). 나머지는 /signin 으로.
        // 루프 방지: /signin 은 resolveGuestRedirect 가 항상 null(통과)을 돌려준다.
        const guestRedirect = resolveGuestRedirect(to.path, RouterEnum.SignIn.path);
        if (guestRedirect) {
            next(guestRedirect);
        } else {
            next();
        }
    }
});

export default router;
